"use client";

import * as React from "react";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar, type CalendarProps } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { addYears, isAfter, isBefore, startOfToday } from "date-fns";

interface SingleDatePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  disabled?: boolean | CalendarProps["disabled"];
  placeholder?: string;
}

export function SingleDatePicker({
  className,
  date,
  onDateChange,
  disabled,
  placeholder = "Pick a date",
}: SingleDatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const today = startOfToday();
  const maxDate = addYears(today, 1);

  const isDisabledProp = typeof disabled === "boolean" && disabled;

  const disabledMatcher = React.useMemo<CalendarProps["disabled"]>(() => {
    const base = (d: Date) => isBefore(d, today) || isAfter(d, maxDate);

    if (!disabled || typeof disabled === "boolean") {
      return base;
    }

    return Array.isArray(disabled) ? [base, ...disabled] : [base, disabled];
  }, [disabled, maxDate, today]);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            disabled={isDisabledProp}
            className={cn(
              "w-[200px] justify-start text-left font-normal",
              !date && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date ? format(date, "LLL dd, y") : <span>{placeholder}</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            autoFocus
            mode="single"
            defaultMonth={date}
            selected={date}
            onSelect={(newDate) => {
              onDateChange(newDate);
              setOpen(false);
            }}
            disabled={disabledMatcher}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
